import { useEffect, useState } from "react";
import type { CSSProperties } from "react";
import { DonationCampaign, getDonationCampaigns } from "../data/donations";
import { getDonationHref } from "../utils/links";
import { trackCta } from "../analytics";
import { ButtonLink } from "./ButtonLink";

type DonationCampaignsProps = {
  featuredOnly?: boolean;
  title?: string;
};

function formatCurrency(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

function percentRaised(campaign: DonationCampaign) {
  if (!campaign.goalAmount) return 0;
  return Math.min(100, Math.round((campaign.raisedAmount / campaign.goalAmount) * 100));
}

function CampaignGauge({ campaign }: { campaign: DonationCampaign }) {
  const percent = percentRaised(campaign);
  const donationHref = getDonationHref();
  const dashboardHref = campaign.liveDashboardEnabled && campaign.liveDashboardSlug ? `/${campaign.liveDashboardSlug}` : "";
  const hasDonationDestination = donationHref !== "/support" && donationHref !== "/giving";
  return (
    <article className="campaign-card">
      <div className="campaign-gauge" style={{ "--progress": `${percent}%` } as CSSProperties}>
        <div className="campaign-gauge-inner">
          <strong>{percent}%</strong>
          <span>funded</span>
        </div>
      </div>
      <div className="campaign-copy">
        <p className="eyebrow">Giving Campaign</p>
        <h3>{campaign.title}</h3>
        {campaign.summary ? <p>{campaign.summary}</p> : null}
        <div className="campaign-meter" aria-label={`${campaign.title} progress`}>
          <span style={{ width: `${percent}%` }} />
        </div>
        <div className="campaign-totals">
          <span><b>{formatCurrency(campaign.raisedAmount)}</b> raised</span>
          <span>{formatCurrency(campaign.goalAmount)} goal</span>
        </div>
        <div className="campaign-actions">
          {dashboardHref ? (
            <ButtonLink href={dashboardHref} variant="gold" onClick={() => trackCta("custom", `Donation Campaign Dashboard: ${campaign.title}`)}>
              View Live Dashboard
            </ButtonLink>
          ) : null}
          {hasDonationDestination ? (
            <ButtonLink href={donationHref} variant={dashboardHref ? "outline" : "gold"} onClick={() => trackCta("donate", `Donation Campaign: ${campaign.title}`)}>
              Donate/Give
            </ButtonLink>
          ) : null}
        </div>
      </div>
    </article>
  );
}

export function DonationCampaigns({ featuredOnly = false, title }: DonationCampaignsProps) {
  const [campaigns, setCampaigns] = useState<DonationCampaign[]>([]);

  useEffect(() => {
    getDonationCampaigns({ featuredOnly, limitCount: featuredOnly ? 1 : 12 })
      .then(setCampaigns)
      .catch(() => setCampaigns([]));
  }, [featuredOnly]);

  if (!campaigns.length) return null;

  return (
    <section className={featuredOnly ? "campaign-feature-band" : "section band-warm"}>
      <div className="container">
        <div className="section-heading">
          <p className="eyebrow">Support ZCA</p>
          <h2>{title || (featuredOnly ? "Help move this campaign forward" : "Current giving opportunities")}</h2>
        </div>
        <div className="campaign-grid">
          {campaigns.map((campaign) => <CampaignGauge key={campaign.id} campaign={campaign} />)}
        </div>
      </div>
    </section>
  );
}
